import { useState } from "react";
import { Input as AntInput, Select as AntSelect, Space } from "antd";
import { get, isNil } from "lodash";
import { BiChevronDown, BiChevronUp, BiTrash } from "react-icons/bi";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Switch from "shared/components/Switch";
import { COLORS } from "shared/style/colors";
import { NODE_TYPES } from "utils/constants";
import { getValidatorsForInputType } from "utils/validation-rules";
import FieldConditionsEditor from "./FieldConditionsEditor";
import FieldInputConfigEditor from "./FieldInputConfigEditor";
import FieldRulesEditor from "./FieldRulesEditor";
import { applyVariableDefaults, buildFormFieldFromVariable } from "./catalog";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const INPUT_TYPES = [
  "auto",
  "text",
  "date",
  "number",
  "slider",
  "email",
  "phone",
];

const keepCompatibleRules = (rules, type) => {
  const allowed = getValidatorsForInputType(type);
  return (rules || []).filter((rule) => allowed.includes(rule.validator));
};

const FieldSection = ({ title, open, onToggle, children }) => (
  <div className="canvas-editor__subsection">
    <div
      className="canvas-editor__row-between canvas-editor__section-title"
      onClick={onToggle}
    >
      <span>{title}</span>
      {open ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
    </div>
    {open ? children : null}
  </div>
);

const JsonField = ({ label, value, onChange }) => {
  const [draft, setDraft] = useState(
    isNil(value) ? "" : JSON.stringify(value, null, 2),
  );
  const [invalid, setInvalid] = useState(false);
  const handle = (event) => {
    const next = event.target.value;
    setDraft(next);
    if (next.trim() === "") {
      setInvalid(false);
      onChange(null);
      return;
    }
    try {
      onChange(JSON.parse(next));
      setInvalid(false);
    } catch (_) {
      setInvalid(true);
    }
  };
  return (
    <div>
      <div className="canvas-editor__label">{label}</div>
      <AntInput.TextArea
        autoSize={{ minRows: 3, maxRows: 10 }}
        value={draft}
        onChange={handle}
        status={invalid ? "error" : ""}
        className="canvas-editor__mono"
      />
    </div>
  );
};

export const FormDefinitionBuilder = ({
  formDefinition,
  variables,
  labels,
  onChange,
}) => {
  const [openSections, setOpenSections] = useState({});
  const base = formDefinition || { fields: [] };
  const definition = {
    ...base,
    strict: isNil(base.strict) ? true : base.strict,
    fields: base.fields || [],
  };
  const fields = definition.fields;
  const usedAccessors = new Set(fields.map((field) => field.accessor));
  const options = (variables || [])
    .filter((variable) => !usedAccessors.has(variable.accessor))
    .map((variable) => ({ value: variable.accessor, label: variable.label }));
  const fieldLabels = labels.FIELD_VALIDATION || labels;

  const emit = (next) => onChange(next);

  const toggleSection = (key) =>
    setOpenSections((current) => ({
      ...current,
      [key]: !current[key],
    }));

  const addField = (accessor) => {
    const variable = (variables || []).find(
      (item) => item.accessor === accessor,
    );
    if (!variable) return;
    emit({
      ...definition,
      fields: [...fields, buildFormFieldFromVariable(variable)],
    });
  };

  const updateField = (index, updater) =>
    emit({
      ...definition,
      fields: fields.map((field, i) => (i === index ? updater(field) : field)),
    });

  const removeField = (index) =>
    emit({ ...definition, fields: fields.filter((_, i) => i !== index) });

  const moveField = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    emit({ ...definition, fields: next });
  };

  return (
    <Space direction="vertical" size={12} className="canvas-editor__full-width">
      <div className="canvas-editor__row">
        <Switch
          checked={!!definition.strict}
          onChange={(checked) => emit({ ...definition, strict: checked })}
        />
        <span>{labels.STRICT}</span>
      </div>
      <AntSelect
        showSearch
        className="canvas-editor__full-width"
        placeholder={labels.ADD_FIELD}
        value={null}
        options={options}
        optionFilterProp="label"
        onChange={addField}
      />
      {fields.length === 0 ? (
        <div className="canvas-editor__meta">{labels.NO_FIELDS}</div>
      ) : (
        fields.map((field, index) => {
          const rebindOptions = (variables || [])
            .filter(
              (variable) =>
                variable.accessor === field.accessor ||
                !usedAccessors.has(variable.accessor),
            )
            .map((variable) => ({
              value: variable.accessor,
              label: variable.label,
            }));

          return (
            <div key={field.accessor || index} className="canvas-editor__card">
              <div className="canvas-editor__row-between mb-6">
                <strong className="canvas-editor__label-strong">
                  {field.label}
                </strong>
                <Space size={4}>
                  <div
                    className="delete-icon"
                    onClick={() => moveField(index, -1)}
                  >
                    <BiChevronUp size={18} />
                  </div>
                  <div
                    className="delete-icon"
                    onClick={() => moveField(index, 1)}
                  >
                    <BiChevronDown size={18} />
                  </div>
                  <div
                    className="delete-icon"
                    onClick={() => removeField(index)}
                  >
                    <BiTrash color={COLORS.C_DANGER} size={18} />
                  </div>
                </Space>
              </div>
              <div className="canvas-editor__meta-xs mb-6">
                {field.accessor}
              </div>
              <div className="canvas-editor__required-row mb-6">
                <Switch
                  checked={!!field.display}
                  onChange={(checked) =>
                    updateField(index, (item) => ({
                      ...item,
                      display: checked,
                      required: checked ? false : item.required,
                    }))
                  }
                />
                <span className="canvas-editor__label">
                  {field.display ? labels.FIELD_DISPLAY : labels.FIELD_INPUT}
                </span>
              </div>
              {!field.display ? (
                <Space
                  direction="vertical"
                  size={8}
                  className="canvas-editor__full-width"
                >
                  <Select
                    showSearch
                    label={labels.FIELD_BINDING}
                    optionFilterProp="label"
                    options={rebindOptions}
                    value={field.accessor}
                    onChange={(accessor) => {
                      const variable = (variables || []).find(
                        (item) => item.accessor === accessor,
                      );
                      if (!variable) return;
                      updateField(index, (item) =>
                        applyVariableDefaults(item, variable),
                      );
                    }}
                  />
                  {field.options && field.options.length ? null : (
                    <div>
                      <div className="canvas-editor__label">
                        {labels.FIELD_TYPE}
                      </div>
                      <AntSelect
                        className="canvas-editor__full-width"
                        value={field.type || "auto"}
                        options={INPUT_TYPES.map((type) => ({
                          value: type,
                          label: get(labels, ["FIELD_TYPES", type], type),
                        }))}
                        onChange={(type) =>
                          updateField(index, (item) => ({
                            ...item,
                            type: type === "auto" ? null : type,
                            inputConfig: {},
                            rules: keepCompatibleRules(item.rules, type),
                          }))
                        }
                      />
                    </div>
                  )}
                  <div className="canvas-editor__required-row">
                    <Switch
                      checked={!!field.required}
                      onChange={(checked) =>
                        updateField(index, (item) => ({
                          ...item,
                          required: checked,
                        }))
                      }
                    />
                    <span className="canvas-editor__label">
                      {labels.REQUIRED}
                    </span>
                  </div>
                  <FieldSection
                    title={fieldLabels.RULES_TITLE}
                    open={!!openSections[`${index}:rules`]}
                    onToggle={() => toggleSection(`${index}:rules`)}
                  >
                    <FieldRulesEditor
                      rules={field.rules || []}
                      variables={variables}
                      labels={fieldLabels}
                      fieldType={field.type || "auto"}
                      onChange={(rules) =>
                        updateField(index, (item) => ({ ...item, rules }))
                      }
                    />
                  </FieldSection>
                  <FieldSection
                    title={fieldLabels.INPUT_CONFIG_TITLE}
                    open={!!openSections[`${index}:input`]}
                    onToggle={() => toggleSection(`${index}:input`)}
                  >
                    <FieldInputConfigEditor
                      value={field.inputConfig || {}}
                      labels={fieldLabels}
                      inputType={field.type || "auto"}
                      onChange={(inputConfig) =>
                        updateField(index, (item) => ({
                          ...item,
                          inputConfig,
                        }))
                      }
                    />
                  </FieldSection>
                  <FieldSection
                    title={fieldLabels.VISIBLE_WHEN_TITLE}
                    open={!!openSections[`${index}:visible`]}
                    onToggle={() => toggleSection(`${index}:visible`)}
                  >
                    <FieldConditionsEditor
                      value={field.visibleWhen || []}
                      variables={variables}
                      labels={fieldLabels}
                      groupMode="flat"
                      onChange={(visibleWhen) =>
                        updateField(index, (item) => ({
                          ...item,
                          visibleWhen: Array.isArray(visibleWhen)
                            ? visibleWhen
                            : visibleWhen && visibleWhen.all
                              ? visibleWhen.all
                              : [],
                        }))
                      }
                    />
                  </FieldSection>
                </Space>
              ) : null}
            </div>
          );
        })
      )}
    </Space>
  );
};

const NodeConfigPanel = ({ nodeType, config = {}, labels, onConfigChange }) => {
  const setField = (key, value) =>
    onConfigChange({ ...(config || {}), [key]: value });

  if (nodeType === NODE_TYPES.API_CALL)
    return (
      <Space
        direction="vertical"
        size={12}
        className="canvas-editor__full-width"
      >
        <Input
          label={labels.URL}
          placeholder={labels.URL}
          value={get(config, "url", "")}
          onChange={(e) => setField("url", e.target.value)}
        />
        <Select
          label={labels.METHOD}
          placeholder={labels.METHOD}
          options={HTTP_METHODS.map((method) => ({
            value: method,
            label: method,
          }))}
          value={get(config, "method") || undefined}
          onChange={(value) => setField("method", value)}
        />
        <JsonField
          label={labels.HEADERS}
          value={get(config, "headers")}
          onChange={(value) => setField("headers", value)}
        />
        <JsonField
          label={labels.BODY}
          value={get(config, "body")}
          onChange={(value) => setField("body", value)}
        />
        <Input
          label={labels.RESULT_KEY}
          placeholder={labels.RESULT_KEY}
          value={get(config, "resultKey", "")}
          onChange={(e) => setField("resultKey", e.target.value)}
        />
      </Space>
    );

  if (nodeType === NODE_TYPES.TIMER)
    return (
      <Input
        type="number"
        label={labels.DELAY_SECONDS}
        placeholder={labels.DELAY_SECONDS}
        value={isNil(get(config, "delaySeconds")) ? "" : config.delaySeconds}
        onChange={(e) =>
          setField(
            "delaySeconds",
            e.target.value === "" ? null : Number(e.target.value),
          )
        }
      />
    );

  if (nodeType === NODE_TYPES.COMMISSION)
    return (
      <Input
        type="number"
        label={labels.COMMISSION}
        placeholder={labels.COMMISSION}
        value={isNil(get(config, "commission")) ? "" : config.commission}
        onChange={(e) =>
          setField(
            "commission",
            e.target.value === "" ? null : Number(e.target.value),
          )
        }
      />
    );

  if (nodeType === NODE_TYPES.NOTIFICATION)
    return (
      <Space
        direction="vertical"
        size={8}
        className="canvas-editor__full-width"
      >
        <Input
          label={labels.CHANNEL}
          placeholder={labels.CHANNEL}
          value={get(config, "channel", "")}
          onChange={(e) => setField("channel", e.target.value)}
        />
        <Input
          label={labels.NOTIFICATION_TITLE}
          placeholder={labels.NOTIFICATION_TITLE}
          value={get(config, "title", "")}
          onChange={(e) => setField("title", e.target.value)}
        />
        <AntInput.TextArea
          rows={3}
          placeholder={labels.NOTIFICATION_CONTENT}
          value={get(config, "content", "")}
          onChange={(e) => setField("content", e.target.value)}
        />
      </Space>
    );

  return (
    <JsonField
      label={labels.JSON_CONFIG}
      value={config}
      onChange={(value) => onConfigChange(value || {})}
    />
  );
};

export default NodeConfigPanel;
